/** Identity for a chat turn returned immediately by POST `/chat`. */
export type PendingTurnIdentity = {
  epochTimeSecs: number;
  chatKey?: string;
};

/** Minimal chat-history row shape for pending-turn polling. */
export type ChatHistoryPollItem = {
  question?: string;
  answer?: string;
  status?: string;
};

type HistoryIdentityRow = {
  epoch_time_secs?: number;
  chat_key?: string;
};

type AnswerIdentitySource = {
  pendingPlaceholder?: boolean;
  question: string;
  epochTimeSecs?: number;
  resultForFeedback?: (ChatHistoryPollItem & HistoryIdentityRow) | null;
};

export function parsePendingTurnIdentityFromAssistantResponse(
  result: Record<string, unknown> | null | undefined,
): PendingTurnIdentity | null {
  const epochTimeSecs = Number(result?.epoch_time_secs);
  if (!Number.isFinite(epochTimeSecs) || epochTimeSecs <= 0) {
    return null;
  }
  const chatKey =
    typeof result?.chat_key === 'string' && result.chat_key.trim() !== ''
      ? result.chat_key
      : undefined;
  return { epochTimeSecs, chatKey };
}

export function historyRowMatchesPendingIdentity(
  row: HistoryIdentityRow,
  identity: PendingTurnIdentity,
): boolean {
  if (row.epoch_time_secs !== identity.epochTimeSecs) {
    return false;
  }
  if (
    identity.chatKey &&
    row.chat_key &&
    row.chat_key !== identity.chatKey
  ) {
    return false;
  }
  return true;
}

export function answerMatchesPendingIdentity(
  answer: AnswerIdentitySource,
  identity: PendingTurnIdentity,
): boolean {
  if (answer.pendingPlaceholder) {
    return false;
  }
  const fromFeedback = answer.resultForFeedback;
  if (fromFeedback?.epoch_time_secs != null) {
    return historyRowMatchesPendingIdentity(fromFeedback, identity);
  }
  if (answer.epochTimeSecs != null && answer.epochTimeSecs > 0) {
    return historyRowMatchesPendingIdentity(
      { epoch_time_secs: answer.epochTimeSecs, chat_key: fromFeedback?.chat_key },
      identity,
    );
  }
  return false;
}

export function findAnswerMatchingPendingIdentity<T extends AnswerIdentitySource>(
  formatted: T[],
  identity: PendingTurnIdentity,
): T | undefined {
  return [...formatted]
    .reverse()
    .find((answer) => answerMatchesPendingIdentity(answer, identity));
}

/** Align with answer-item display so optimistic merge matches server-stored text. */
export function normalizeQuestionForMerge(q: string): string {
  return q
    .replace(/^strands-/, '')
    .replace(/^threat-report-/, '')
    .trim();
}

/**
 * Returns true while the backend may still be streaming / replacing a placeholder answer.
 * Mirrors the polling conditions in `components/answer-item.tsx`.
 */
export function shouldPollChatHistoryItem(item: ChatHistoryPollItem): boolean {
  const status = typeof item.status === 'string' ? item.status : 'unknown';

  if (status === 'error') return false;
  if (status === 'completed') return false;

  let answer: Record<string, unknown>;
  try {
    answer = JSON.parse(item.answer ?? '') as Record<string, unknown>;
  } catch {
    return status === 'initializing' || status === 'inprogress';
  }

  const isInitializing = status === 'initializing';
  const isInProgress = status === 'inprogress';
  const isPreliminary =
    'is_preliminary' in answer && answer.is_preliminary === true;
  const containsMessages =
    Array.isArray(answer.messages) && answer.messages.length > 0;

  const isPreliminaryResult = isInitializing || isInProgress || isPreliminary;

  const isFinalResult =
    status === 'completed' ||
    (containsMessages && !isPreliminaryResult) ||
    !containsMessages;

  if (isPreliminaryResult || !isFinalResult || isInitializing) {
    return true;
  }

  const output = typeof answer.output === 'string' ? answer.output.trim() : '';
  if (
    output.length > 0 &&
    output.length < 2500 &&
    /^\s*Analyzing documents/i.test(output)
  ) {
    return true;
  }

  return false;
}

export function isOptimisticServerCaughtUp<T extends AnswerIdentitySource>(
  formatted: T[],
  pending: AnswerIdentitySource,
  identity: PendingTurnIdentity | null,
): boolean {
  if (identity) {
    return !!findAnswerMatchingPendingIdentity(formatted, identity);
  }
  if (formatted.length === 0) {
    return false;
  }
  const last = formatted[formatted.length - 1];
  return (
    normalizeQuestionForMerge(last.question) ===
      normalizeQuestionForMerge(pending.question) &&
    (last.epochTimeSecs ?? 0) > 0 &&
    !last.pendingPlaceholder &&
    !!last.resultForFeedback
  );
}

/**
 * Appends the optimistic placeholder to a server-formatted list, or strips
 * placeholders once the server row is present. Idempotent when `formatted`
 * already contains a placeholder (avoids duplicate loading rows on no-op refresh).
 */
export function mergeFormattedAnswersWithOptimisticPending<
  T extends AnswerIdentitySource,
>(
  formatted: T[],
  pending: T | null | undefined,
  identity: PendingTurnIdentity | null,
): T[] {
  if (!pending?.pendingPlaceholder) {
    return formatted;
  }
  const serverCaughtUp = isOptimisticServerCaughtUp(
    formatted,
    pending,
    identity,
  );
  if (serverCaughtUp) {
    return formatted.filter((answer) => !answer.pendingPlaceholder);
  }
  if (formatted.some((answer) => answer.pendingPlaceholder)) {
    return formatted;
  }
  return [...formatted, pending];
}

/** Delay before the first chat-history poll after POST `/chat`. */
export const CHAT_HISTORY_FIRST_POLL_DELAY_MS = 6000;

/** Delay between subsequent chat-history polls while a turn is in flight. */
export const CHAT_HISTORY_POLL_INTERVAL_MS = 3000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function findFallbackPendingAnswer<T extends AnswerIdentitySource>(
  formatted: T[],
  pendingQuestion: string,
): T | undefined {
  const normalizedPending = normalizeQuestionForMerge(pendingQuestion);
  return [...formatted]
    .reverse()
    .find(
      (answer) =>
        !answer.pendingPlaceholder &&
        !!answer.resultForFeedback &&
        normalizeQuestionForMerge(answer.question) === normalizedPending,
    );
}

export async function pollUntilLatestAnswerSettled(
  load: () => Promise<AnswerIdentitySource[]>,
  identity: PendingTurnIdentity | null,
  pendingQuestion: string,
  // Stop polling when the surrounding context (e.g. the active case/thread) has
  // changed. This prevents a stale `load` closure from continuing to run — and
  // clobbering the freshly-switched context's state — for the full poll window.
  shouldAbort?: () => boolean,
): Promise<void> {
  const maxIterations = 90;
  await sleep(CHAT_HISTORY_FIRST_POLL_DELAY_MS);
  for (let i = 0; i < maxIterations; i++) {
    if (shouldAbort?.()) {
      return;
    }
    const formatted = await load();
    if (shouldAbort?.()) {
      return;
    }
    const matchingServer = identity
      ? findAnswerMatchingPendingIdentity(formatted, identity)
      : findFallbackPendingAnswer(formatted, pendingQuestion);
    if (!matchingServer?.resultForFeedback) {
      await sleep(CHAT_HISTORY_POLL_INTERVAL_MS);
      continue;
    }
    if (!shouldPollChatHistoryItem(matchingServer.resultForFeedback)) {
      return;
    }
    await sleep(CHAT_HISTORY_POLL_INTERVAL_MS);
  }
}
